import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './Auth.css';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Şifremi Unuttum Modal State
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState('');

  // 🆕 Turnstile state
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileError, setTurnstileError] = useState(false);
  const turnstileRef = useRef(null);

  // Landing page'den gelen dil bilgisini uygula
  useEffect(() => {
    const savedLanguage = localStorage.getItem('rux_language');
    if (savedLanguage && savedLanguage !== i18n.language) {
      i18n.changeLanguage(savedLanguage);
    }
  }, [i18n]);

  // 🆕 Turnstile script'ini yükle
  useEffect(() => {
    // Turnstile script'ini head'e ekle
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    // Script yüklendiğinde Turnstile widget'ını render et
    script.onload = () => {
      if (window.turnstile && turnstileRef.current) {
        window.turnstile.render(turnstileRef.current, {
          sitekey: '0x4AAAAAACdMhpJh8A_wA50i', // 🔑 Kendi site key'inizi buraya yazın
          callback: (token) => {
            setTurnstileToken(token);
            setTurnstileError(false);
          },
          'error-callback': () => {
            setTurnstileError(true);
            setTurnstileToken('');
          },
          'expired-callback': () => {
            setTurnstileToken('');
          },
          theme: 'light', // veya 'dark'
          size: 'normal', // veya 'compact'
        });
      }
    };

    return () => {
      // Cleanup
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // 🆕 Turnstile token kontrolü
    if (!turnstileToken) {
      setError(t('error.captchaRequired') || 'Please complete the captcha verification');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('/auth/login', { 
        email, 
        password,
        turnstileToken // 🆕 Turnstile token'ı backend'e gönder
      });
      
      if (response.data.success) {
        // Login başarılı, ana sayfaya yönlendir
        window.location.href = '/dashboard';
      } else {
        setError(t('error.loginFailed'));
        // 🆕 Başarısız login'de Turnstile'ı resetle
        resetTurnstile();
      }
    } catch (err) {
      console.error('Login error:', err);
      const errorCode = err.response?.data?.error_code;
      setError(errorCode ? t(`error.${errorCode}`) : t('error.loginFailed'));
      // 🆕 Hata durumunda Turnstile'ı resetle
      resetTurnstile();
    } finally {
      setLoading(false);
    }
  };

  // 🆕 Turnstile widget'ını resetle
  const resetTurnstile = () => {
    if (window.turnstile) {
      window.turnstile.reset();
      setTurnstileToken('');
    }
  };

  // Şifremi Unuttum - Modal Aç
  const handleForgotPasswordClick = () => {
    setShowForgotPasswordModal(true);
    setForgotPasswordEmail('');
    setForgotPasswordError('');
    setForgotPasswordSuccess('');
  };

  // Şifremi Unuttum - Modal Kapat
  const handleCloseForgotPasswordModal = () => {
    setShowForgotPasswordModal(false);
    setForgotPasswordEmail('');
    setForgotPasswordError('');
    setForgotPasswordSuccess('');
  };

  // Şifremi Unuttum - Form Submit
  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setForgotPasswordError('');
    setForgotPasswordSuccess('');

    if (!forgotPasswordEmail) {
      setForgotPasswordError(t('error.emailRequired'));
      return;
    }

    setForgotPasswordLoading(true);

    try {
      const response = await axios.post('/auth/forgot-password', {
        email: forgotPasswordEmail
      });

      if (response.data.success) {
        setForgotPasswordSuccess(t(`success.${response.data.message_code}`));
        setTimeout(() => {
          handleCloseForgotPasswordModal();
        }, 3000);
      } else {
        setForgotPasswordError(t('error.passwordResetRequestFailed'));
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      const errorCode = err.response?.data?.error_code;
      
      if (errorCode === 'TOO_MANY_RESET_REQUESTS') {
        setForgotPasswordError(t(`error.${errorCode}`));
      } else {
        setForgotPasswordError(errorCode ? t(`error.${errorCode}`) : t('error.passwordResetRequestFailed'));
      }
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  // Ana sayfaya dön butonu
  const handleBackToHome = () => {
    window.location.href = '/';
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>{t('app.name')}</h1>
          <p>{t('login.tagline')}</p>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">{t('auth.email')}</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ornek@email.com"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">{t('auth.password')}</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </div>
          
          {/* Şifremi Unuttum Linki */}
          <div className="forgot-password-link" style={{ textAlign: 'right', marginBottom: '15px' }}>
            <button
              type="button"
              onClick={handleForgotPasswordClick}
              style={{
                background: 'none',
                border: 'none',
                color: '#007bff',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontSize: '14px',
                padding: 0
              }}
            >
              {t('auth.forgotPassword')}
            </button>
          </div>

          {/* 🆕 Cloudflare Turnstile Widget */}
          <div className="form-group cf">
            <div ref={turnstileRef}></div>
            {turnstileError && (
              <div style={{ color: '#d9534f', fontSize: '14px', marginTop: '5px' }}>
                {t('error.captchaFailed') || 'Captcha verification failed. Please try again.'}
              </div>
            )}
          </div>
          
          {error && <div className="error-message">{error}</div>}
          
          <button 
            type="submit" 
            disabled={loading || !turnstileToken} 
            className="btn-primary"
            style={{
              opacity: (loading || !turnstileToken) ? 0.6 : 1,
              cursor: (loading || !turnstileToken) ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? t('auth.loggingIn') : t('auth.login')}
          </button>
        </form>
        
        <div className="auth-footer">
          {t('auth.noAccount')}{' '}
          <Link to="/register">{t('auth.register')}</Link>
        </div>

        {/* Ana sayfaya dön linki */}
        <div className="auth-footer" style={{ marginTop: '10px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
          <button 
            onClick={handleBackToHome}
            style={{
              background: 'none',
              border: 'none',
              color: '#666',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontSize: '14px'
            }}
          >
            ← {t('auth.backToHome') || 'Ana Sayfaya Dön'}
          </button>
        </div>
      </div>

      {/* Şifremi Unuttum Modal */}
      {showForgotPasswordModal && (
        <div className="modal-overlay" onClick={handleCloseForgotPasswordModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('auth.forgotPassword')}</h2>
              <button
                className="modal-close"
                onClick={handleCloseForgotPasswordModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            
            <div className="modal-body dflex-col">
              <p style={{ marginBottom: '20px', color: '#666', fontSize: '14px' }}>
                {t('auth.forgotPasswordDescription')}
              </p>
              
              <form onSubmit={handleForgotPasswordSubmit} className="dflex-col padzero">
                <div className="form-group">
                  <label htmlFor="forgotPasswordEmail">{t('auth.email')}</label>
                  <input
                    id="forgotPasswordEmail"
                    type="email"
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    placeholder="ornek@email.com"
                    required
                    disabled={forgotPasswordLoading}
                  />
                </div>
                
                {forgotPasswordError && (
                  <div className="error-message">{forgotPasswordError}</div>
                )}
                
                {forgotPasswordSuccess && (
                  <div className="success-message">{forgotPasswordSuccess}</div>
                )}
                
                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button
                    type="button"
                    onClick={handleCloseForgotPasswordModal}
                    disabled={forgotPasswordLoading}
                    style={{
                      flex: 1,
                      padding: '10px',
                      border: '1px solid #ddd',
                      background: '#fff',
                      cursor: 'pointer',
                      borderRadius: '4px'
                    }}
                  >
                    {t('auth.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={forgotPasswordLoading}
                    className="btn-primary"
                    style={{ flex: 1 }}
                  >
                    {forgotPasswordLoading ? t('auth.sending') : t('auth.sendResetLink')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;
